import type { ActionBinding, VisibilityCondition } from '@json-render/core';

import type { CatalogPropsMap, Spec } from './catalog';

/** Extra element-level options beyond type/props/children. */
export interface SpecBuilderElementOptions {
  children?: string[];
  /** Visibility condition for the element. */
  visible?: VisibilityCondition;
  /** Event-to-action bindings (e.g. `{ press: { action: 'doThing' } }`). */
  on?: Record<string, ActionBinding | ActionBinding[]>;
}

/** Props arg is optional when all fields are optional (e.g. Root, Divider). */
export type SpecBuilderArgs<T extends keyof CatalogPropsMap> =
  {} extends CatalogPropsMap[T]
    ? [props?: CatalogPropsMap[T], children?: string[]]
    : [props: CatalogPropsMap[T], children?: string[]];

/** Ergonomic builder for constructing json-render Spec objects directly. */
export class SpecBuilder {
  private elements: Record<
    string,
    {
      type: string;
      props: Record<string, unknown>;
      children: string[];
      visible?: VisibilityCondition;
      on?: Record<string, ActionBinding | ActionBinding[]>;
    }
  > = {};
  private counter = 0;

  /** Add an element and return its key. Accepts optional element-level options. */
  add<T extends keyof CatalogPropsMap>(
    type: T & string,
    props?: CatalogPropsMap[T],
    options?: SpecBuilderElementOptions | string[],
  ): string {
    // When a plain string[] is passed as the third arg, treat it as children shorthand.
    const opts: SpecBuilderElementOptions = Array.isArray(options)
      ? { children: options }
      : options ?? {};

    const key = `el-${this.counter++}`;
    const element: (typeof this.elements)[string] = {
      type,
      props: (props ?? {}) as Record<string, unknown>,
      children: opts.children ?? [],
    };
    if (opts.visible !== undefined) element.visible = opts.visible;
    if (opts.on) element.on = opts.on;
    this.elements[key] = element;
    return key;
  }

  /** Wrap the given root children in a Root element and return the Spec */
  build(rootChildren: string[]): Spec {
    this.elements['root'] = {
      type: 'Root',
      props: {},
      children: rootChildren,
    };
    return { root: 'root', elements: this.elements };
  }

  /** Build with initial state for interactive specs. */
  buildWithState(rootChildren: string[], state: Record<string, unknown>): Spec {
    const spec = this.build(rootChildren);
    return { ...spec, state };
  }
}
